var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useContext, useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useIntl } from 'react-intl';
import { getEnvironment, GTM_IDS, hasCookieForMobile, INTL_LOCALES, IR_CLICK_ID_COOKIE_NAME, isCSR, isCustomerLoggedIn, isDEURegion, isUSARegion, storeBrokerInCookies, storeClickId, storeRafIdInCookies, UC_USER_INTERACTION_KEY, useFeatureFlag, } from '@raisin/cp-utils';
import { identifyUserBy, initializeTracking } from '@raisin/events-tracking';
import { HeaderDataContext } from '../../context';
import CountryPickerBanner from './CountryPickerBanner';
import Head from './Head';
import Script from './Script';
import { LogoutCountdown, UnifiedHeader } from './UnifiedHeader';
const initializeTagManager = () => {
    let gtmId = GTM_IDS.non_de_or_at;
    if (isDEURegion()) {
        gtmId = GTM_IDS.de_or_at;
    }
    else if (isUSARegion()) {
        gtmId = GTM_IDS.usa;
    }
    const tagManagerArgs = {
        gtmId,
        dataLayer: {
            environment: getEnvironment(),
            testing: 'GTM testing',
        },
    };
    TagManager.initialize(tagManagerArgs);
};
const gtmDefaultConsentCountries = [
    INTL_LOCALES.UK,
    INTL_LOCALES.IRELAND,
    INTL_LOCALES.FRANCE,
    INTL_LOCALES.US,
    INTL_LOCALES.SPAIN,
    INTL_LOCALES.NETHERLANDS,
    INTL_LOCALES.POLAND,
    INTL_LOCALES.AUSTRIA,
    INTL_LOCALES.GERMANY,
];
const Header = ({ pageTitle, seoSettings, isCountrySelectorPage, isOpenbanking, isRaisinBankNavigationEnabled, tagline, }) => {
    var _a, _b;
    const intl = useIntl();
    let hasDefaultGTMConsent = false;
    if (gtmDefaultConsentCountries.includes(intl.locale)) {
        hasDefaultGTMConsent = true;
    }
    const { customerData } = useContext(HeaderDataContext);
    const [shouldDisplayMobileBanner, setShouldDisplayMobileBanner] = useState(true);
    const { variation } = useFeatureFlag();
    const userManagementCallObsLogout = (_a = variation === null || variation === void 0 ? void 0 : variation('userManagementCallObsLogout', false)) !== null && _a !== void 0 ? _a : false;
    const isLoggedIn = isCustomerLoggedIn();
    const umtFECallKeycloakLogoutOnSessionTimeout = (_b = variation === null || variation === void 0 ? void 0 : variation('umtFECallKeycloakLogoutOnSessionTimeout', false)) !== null && _b !== void 0 ? _b : false;
    const waitForUCConsentAndStoreClickId = (userInteraction) => {
        window.addEventListener('UC_UI_INITIALIZED', () => {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                yield storeClickId(userInteraction);
            }))();
        });
    };
    useEffect(() => {
        if (!isCSR()) {
            return;
        }
        initializeTagManager();
        const originalSetItem = Storage.prototype.setItem;
        Storage.prototype.setItem = function (key, value) {
            if (key === UC_USER_INTERACTION_KEY) {
                const event = new Event('itemInserted');
                event['value'] = value;
                event['key'] = key;
                event['prevValue'] = localStorage.getItem(key);
                window.dispatchEvent(event);
            }
            originalSetItem.apply(this, [key, value]);
        };
    }, []);
    useEffect(() => {
        if (hasCookieForMobile()) {
            setShouldDisplayMobileBanner(false);
        }
    }, []);
    useEffect(() => {
        initializeTracking(intl.locale);
    }, [intl.locale]);
    useEffect(() => {
        if (customerData) {
            identifyUserBy('bac_number', customerData.id);
        }
    }, [customerData]);
    useEffect(() => {
        if (!isCSR()) {
            return;
        }
        const handleStorageChange = (e) => {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.has(IR_CLICK_ID_COOKIE_NAME)) {
                if (e.prevValue === 'false') {
                    (() => __awaiter(void 0, void 0, void 0, function* () {
                        yield storeClickId(e.value);
                    }))();
                    return;
                }
                waitForUCConsentAndStoreClickId(e.value);
            }
        };
        window.addEventListener('itemInserted', handleStorageChange, false);
        return () => window.removeEventListener('itemInserted', handleStorageChange);
    }, []);
    useEffect(() => {
        const checkLocalStorage = () => {
            const value = localStorage.getItem(UC_USER_INTERACTION_KEY);
            const urlParams = new URLSearchParams(window.location.search);
            if (value === 'true' && urlParams.has(IR_CLICK_ID_COOKIE_NAME)) {
                (() => __awaiter(void 0, void 0, void 0, function* () {
                    waitForUCConsentAndStoreClickId(value);
                }))();
            }
        };
        checkLocalStorage();
        window.addEventListener('storage', checkLocalStorage);
        return () => {
            window.removeEventListener('storage', checkLocalStorage);
        };
    }, []);
    useEffect(() => {
        storeRafIdInCookies();
        storeBrokerInCookies();
    }, []);
    const shouldDisplayCountdown = isLoggedIn && !isOpenbanking;
    const showCountryPickerBanner = !isLoggedIn && !isRaisinBankNavigationEnabled && !isCountrySelectorPage;
    return (React.createElement(React.Fragment, null,
        React.createElement(Head, { pageTitle: pageTitle, seoSettings: seoSettings, tagline: tagline }),
        React.createElement(Script, { hasDefaultGTMConsent: hasDefaultGTMConsent }),
        showCountryPickerBanner && React.createElement(CountryPickerBanner, null),
        React.createElement("div", null,
            shouldDisplayMobileBanner ? React.createElement(UnifiedHeader, { isLoggedIn: isLoggedIn }) : null,
            shouldDisplayCountdown ? (React.createElement(LogoutCountdown, { platform: "customer-portal", flags: {
                    userManagementCallObsLogout,
                    umtFECallKeycloakLogoutOnSessionTimeout,
                    umtCallOBSLoginEndpointFromFE: false,
                } })) : null)));
};
export default Header;
//# sourceMappingURL=index.js.map